import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { LoadingState } from '@/components/ui/loading-state';
import { NotFoundPage } from './not-found.js';
import { BrokerLandingPage } from './public/broker-landing.js';
import { PrivacyPolicyPage } from './public/privacy-policy.js';
import { PropertyDetailPage } from './public/property-detail.js';
import { QualificationFormEnginePage } from './public/qualification-form-engine.js';
import { QualificationResultPage } from './public/qualification-result.js';

// Login, painel e admin (e o Firebase junto) só baixam quando alguém abre
// essas rotas. Quem chega do anúncio baixa só a landing.
function lazyNamed<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType<any> })));
}

const AuthShell = lazyNamed(() => import('./auth-shell.js'), 'AuthShell');
const ProtectedRoute = lazyNamed(() => import('@/components/protected-route'), 'ProtectedRoute');
const LoginPage = lazyNamed(() => import('./login.js'), 'LoginPage');
const AdminLayout = lazyNamed(() => import('./admin/layout.js'), 'AdminLayout');
const AdminDashboardPage = lazyNamed(() => import('./admin/dashboard.js'), 'AdminDashboardPage');
const AdminLeadDetailPage = lazyNamed(
  () => import('./admin/lead-detail.js'),
  'AdminLeadDetailPage',
);
const AdminLeadsListPage = lazyNamed(() => import('./admin/leads-list.js'), 'AdminLeadsListPage');
const AdminPerformancePage = lazyNamed(
  () => import('./admin/performance.js'),
  'AdminPerformancePage',
);
const BrokersListPage = lazyNamed(() => import('./admin/brokers-list.js'), 'BrokersListPage');
const EditBrokerPage = lazyNamed(() => import('./admin/broker-edit.js'), 'EditBrokerPage');
const NewBrokerPage = lazyNamed(() => import('./admin/broker-new.js'), 'NewBrokerPage');
const BrokerLayout = lazyNamed(() => import('./broker/layout.js'), 'BrokerLayout');
const BrokerDashboardPage = lazyNamed(() => import('./broker/dashboard.js'), 'BrokerDashboardPage');
const LeadsListPage = lazyNamed(() => import('./broker/leads-list.js'), 'LeadsListPage');
const LeadDetailPage = lazyNamed(() => import('./broker/lead-detail.js'), 'LeadDetailPage');
const MyPropertiesPage = lazyNamed(() => import('./broker/properties-list.js'), 'MyPropertiesPage');
const PropertyEditPage = lazyNamed(() => import('./broker/property-edit.js'), 'PropertyEditPage');
const PageContentEditPage = lazyNamed(
  () => import('./broker/page-content.js'),
  'PageContentEditPage',
);
const BrokerProfilePage = lazyNamed(() => import('./broker/profile.js'), 'BrokerProfilePage');

const pageFallback = (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <LoadingState size="lg" />
  </div>
);

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={pageFallback}>
        <Routes>
          {/* Public · broker landing */}
          <Route path="/" element={<BrokerLandingPage />} />
          <Route path="/empreendimento/:slug" element={<PropertyDetailPage />} />
          {/* Canonical qualification routes · render via the engine, which
            resolves the right FormConfig from the broker's verticalKey. MCMV
            brokers see the MCMV form, PV brokers see the proteção-veicular
            form, etc. Behavior for MCMV is 1:1 with the pre-multi-vertical
            page (verified by the parity tests in apps/api/scripts). */}
          <Route
            path="/empreendimento/:slug/qualificar"
            element={<QualificationFormEnginePage />}
          />
          <Route path="/qualificar" element={<QualificationFormEnginePage />} />
          <Route path="/resultado" element={<QualificationResultPage />} />
          <Route path="/privacidade" element={<PrivacyPolicyPage />} />

          {/* Login e painéis: AuthProvider (Firebase) só aqui dentro */}
          <Route element={<AuthShell />}>
            <Route path="/login" element={<LoginPage />} />

            {/* Broker panel */}
            <Route
              path="/painel"
              element={
                <ProtectedRoute role="broker">
                  <BrokerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<BrokerDashboardPage />} />
              <Route path="leads" element={<LeadsListPage />} />
              <Route path="leads/:id" element={<LeadDetailPage />} />
              <Route path="empreendimentos" element={<MyPropertiesPage />} />
              <Route path="empreendimentos/novo" element={<PropertyEditPage />} />
              <Route path="empreendimentos/:id" element={<PropertyEditPage />} />
              <Route path="minha-pagina" element={<PageContentEditPage />} />
              <Route path="perfil" element={<BrokerProfilePage />} />
            </Route>

            {/* Admin panel */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="leads" element={<AdminLeadsListPage />} />
              <Route path="leads/:id" element={<AdminLeadDetailPage />} />
              <Route path="performance" element={<AdminPerformancePage />} />
              <Route path="corretores" element={<BrokersListPage />} />
              <Route path="corretores/novo" element={<NewBrokerPage />} />
              <Route path="corretores/:id" element={<EditBrokerPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
